import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// The tests render this component with tsx, which has no root tsconfig and would fall back to the classic
// JSX runtime (`React.createElement`); pin the automatic runtime here (tsc's jsx: react-jsx agrees).
/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import X from 'lucide-react/dist/esm/icons/x.mjs';
import { PULSE_CARD_PLACEHOLDER, PULSE_CARD_QUESTION, PULSE_NARROW_PX, PULSE_RATING_LABELS, PULSE_RATINGS, PULSE_SEND_FAILED_TEXT, PULSE_SEND_LABEL, PULSE_THANKS_MS, PULSE_THANKS_TEXT, PULSE_THUMB_PLACEHOLDER, PULSE_UNDO_LABEL, } from './pulseCards.js';
import { PULSE_COMMENT_MAX_CHARS, PULSE_TAGS_MAX } from '../client.js';
/** Whether the element is narrower than the wrap point (no layout in tests: a zero width is not narrow). */
function narrowWidth(element) {
    if (!element)
        return false;
    const width = element.getBoundingClientRect().width || element.clientWidth || element.offsetWidth;
    return width > 0 && width < PULSE_NARROW_PX;
}
/** Re-measures `ref` when its box changes (ResizeObserver where there is one) and says whether it is narrow. */
function useNarrow(ref) {
    const [narrow, setNarrow] = useState(false);
    useLayoutEffect(() => {
        const element = ref.current;
        if (!element)
            return undefined;
        const measure = () => setNarrow(narrowWidth(element));
        measure();
        const Observer = typeof ResizeObserver === 'function' ? ResizeObserver : undefined;
        if (!Observer) {
            window.addEventListener('resize', measure);
            return () => window.removeEventListener('resize', measure);
        }
        const observer = new Observer(measure);
        observer.observe(element);
        return () => observer.disconnect();
    }, [ref]);
    return narrow;
}
export function PulseCard({ askId, actionId, summary, tool, chips, perDayLeft, onSend, onDismiss, onDone, thanksMs = PULSE_THANKS_MS }) {
    const [rating, setRating] = useState(null);
    const [undo, setUndo] = useState(false);
    const [tags, setTags] = useState([]);
    const [comment, setComment] = useState('');
    const [phase, setPhase] = useState('ask');
    const [failed, setFailed] = useState(false);
    const rootRef = useRef(null);
    const inputRef = useRef(null);
    const narrow = useNarrow(rootRef);
    const doneRef = useRef(onDone);
    doneRef.current = onDone;
    useEffect(() => {
        if (phase !== 'thanks')
            return undefined;
        const timer = setTimeout(() => { setPhase('gone'); doneRef.current?.(); }, thanksMs);
        return () => clearTimeout(timer);
    }, [phase, thanksMs]);
    const choose = useCallback((next) => {
        if (phase !== 'ask')
            return;
        setRating(next);
        // The comment is the natural next step: Enter there sends.
        requestAnimationFrame(() => inputRef.current?.focus());
    }, [phase]);
    const toggleTag = (tag) => setTags((current) => current.includes(tag) ? current.filter((item) => item !== tag) : current.length >= PULSE_TAGS_MAX ? current : [...current, tag]);
    const send = async () => {
        if (phase !== 'ask' || !rating)
            return;
        setPhase('sending');
        setFailed(false);
        const body = { askId, kind: 'card', rating, undo, ...(actionId ? { actionId } : {}), ...(tags.length ? { tags: tags.slice(0, PULSE_TAGS_MAX) } : {}), ...(comment.trim() ? { comment: comment.trim().slice(0, PULSE_COMMENT_MAX_CHARS) } : {}) };
        let outcome;
        try {
            outcome = await onSend(body);
        }
        catch {
            outcome = { ok: false, status: 0 };
        }
        if (outcome && outcome.ok === false) {
            setFailed(true);
            setPhase('ask');
            return;
        }
        setPhase('thanks');
    };
    const onKeyDown = (event) => {
        if (phase !== 'ask')
            return;
        if (event.key === 'Escape') {
            event.preventDefault();
            onDismiss();
            return;
        }
        const inInput = event.target === inputRef.current;
        if (inInput) {
            if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
                event.preventDefault();
                void send();
            }
            return;
        }
        const index = ['1', '2', '3'].indexOf(event.key);
        if (index >= 0) {
            event.preventDefault();
            choose(PULSE_RATINGS[index]);
        }
    };
    if (phase === 'gone')
        return null;
    const showChips = (rating === 'failed' || undo) && chips.length > 0;
    return (_jsx("div", { ref: rootRef, className: "pulse-card", role: "group", "aria-label": summary, tabIndex: -1, "data-ask-id": askId, "data-action-id": actionId, "data-tool": tool, "data-per-day-left": perDayLeft, "data-narrow": narrow ? 'true' : undefined, "data-phase": phase, onKeyDown: onKeyDown, children: phase === 'thanks' ? (_jsx("div", { className: "pulse-card-thanks", role: "status", children: PULSE_THANKS_TEXT })) : (_jsxs(_Fragment, { children: [_jsxs("div", { className: "pulse-card-row pulse-card-head", children: [_jsx("span", { className: "pulse-card-question", children: PULSE_CARD_QUESTION }), _jsx("span", { className: "pulse-card-buttons", role: "radiogroup", "aria-label": PULSE_CARD_QUESTION, children: PULSE_RATINGS.map((value, index) => (_jsx("button", { type: "button", role: "radio", "aria-checked": rating === value, className: `gyro-btn${rating === value ? '' : ' ghost'} pulse-card-rating`, "data-rating": value, title: `${PULSE_RATING_LABELS[value]} (${index + 1})`, disabled: phase !== 'ask', onClick: () => choose(value), children: PULSE_RATING_LABELS[value] }, value))) }), _jsxs("label", { className: "pulse-card-undo", children: [_jsx("input", { type: "checkbox", checked: undo, disabled: phase !== 'ask', onChange: (event) => setUndo(event.target.checked) }), _jsx("span", { children: PULSE_UNDO_LABEL })] }), _jsx("button", { type: "button", className: "gyro-btn ghost pulse-card-dismiss", onClick: onDismiss, "aria-label": "Dismiss", title: "Dismiss", disabled: phase !== 'ask', children: _jsx(X, { size: 13 }) })] }), showChips && (_jsx("div", { className: "pulse-card-chips", role: "group", "aria-label": "What went wrong", children: chips.slice(0, 3).map((chip) => (_jsx("button", { type: "button", className: "gyro-chip", "aria-pressed": tags.includes(chip), disabled: phase !== 'ask', onClick: () => toggleTag(chip), children: chip }, chip))) })), rating && (_jsxs("div", { className: "pulse-card-row pulse-card-comment", children: [_jsx("input", { ref: inputRef, type: "text", className: "gyro-textinput", placeholder: PULSE_CARD_PLACEHOLDER, "aria-label": PULSE_CARD_PLACEHOLDER, maxLength: PULSE_COMMENT_MAX_CHARS, value: comment, disabled: phase !== 'ask', onChange: (event) => setComment(event.target.value) }), _jsx("button", { type: "button", className: "gyro-btn pulse-card-send", disabled: phase !== 'ask', onClick: () => void send(), children: phase === 'sending' ? 'Sending…' : PULSE_SEND_LABEL })] })), failed && _jsx("div", { className: "pulse-card-failed", role: "alert", children: PULSE_SEND_FAILED_TEXT })] })) }));
}
export function PulseThumbBox({ askId, thumb, onSend, onClose, thanksMs = PULSE_THANKS_MS }) {
    const [comment, setComment] = useState('');
    const [phase, setPhase] = useState('ask');
    const [failed, setFailed] = useState(false);
    const inputRef = useRef(null);
    const closeRef = useRef(onClose);
    closeRef.current = onClose;
    useEffect(() => { inputRef.current?.focus(); }, []);
    useEffect(() => {
        if (phase !== 'thanks')
            return undefined;
        const timer = setTimeout(() => { setPhase('gone'); closeRef.current(); }, thanksMs);
        return () => clearTimeout(timer);
    }, [phase, thanksMs]);
    const send = async () => {
        if (phase !== 'ask')
            return;
        setPhase('sending');
        setFailed(false);
        const text = comment.trim().slice(0, PULSE_COMMENT_MAX_CHARS);
        let outcome;
        try {
            outcome = await onSend({ askId, kind: 'thumb', thumb, ...(text ? { comment: text } : {}) });
        }
        catch {
            outcome = { ok: false, status: 0 };
        }
        if (outcome && outcome.ok === false) {
            setFailed(true);
            setPhase('ask');
            return;
        }
        setPhase('thanks');
    };
    if (phase === 'gone')
        return null;
    return (_jsx("div", { className: "pulse-thumb-box", role: "group", "aria-label": PULSE_THUMB_PLACEHOLDER, "data-thumb": thumb, "data-phase": phase, children: phase === 'thanks' ? (_jsx("span", { className: "pulse-card-thanks", role: "status", children: PULSE_THANKS_TEXT })) : (_jsxs(_Fragment, { children: [_jsx("input", { ref: inputRef, type: "text", className: "gyro-textinput", placeholder: PULSE_THUMB_PLACEHOLDER, "aria-label": PULSE_THUMB_PLACEHOLDER, maxLength: PULSE_COMMENT_MAX_CHARS, value: comment, disabled: phase !== 'ask', onChange: (event) => setComment(event.target.value), onKeyDown: (event) => {
                        if (event.key === 'Escape') {
                            event.preventDefault();
                            onClose();
                        }
                        else if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
                            event.preventDefault();
                            void send();
                        }
                    } }), _jsx("button", { type: "button", className: "gyro-btn pulse-card-send", disabled: phase !== 'ask', onClick: () => void send(), children: phase === 'sending' ? 'Sending…' : PULSE_SEND_LABEL }), _jsx("button", { type: "button", className: "gyro-btn ghost pulse-card-dismiss", onClick: onClose, "aria-label": "Dismiss", title: "Dismiss", disabled: phase !== 'ask', children: _jsx(X, { size: 13 }) }), failed && _jsx("span", { className: "pulse-card-failed", role: "alert", children: PULSE_SEND_FAILED_TEXT })] })) }));
}
//# sourceMappingURL=PulseCard.js.map